/* eslint-disable max-lines */
import styled from '@emotion/styled';
import { mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import Tag from 'aquilla/core/atoms/Tag';
import React, { useRef, useState } from 'react';
import CheckContained from 'aquilla/icons/misc/CheckContained';
import Divider from 'aquilla/core/atoms/Divider';
import ClassVideoPlayer from '@cont/ClassVideoPlayer/ClassVideoPlayer';
import Carousel, { ArrowButton } from 'aquilla/core/molecules/Carousel';
import SkeletonCore from 'aquilla/core/atoms/Skeleton';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import ClassCardAquila from 'aquilla/core/organisms/ClassCard';
import { getImage } from '@utils/imageHelper';
import { getAnimationPath } from '@utils/animationHelpers';
import Dynamic from '@base/Dynamic/Dynamic';
import deviceHelper from '@utils/deviceHelper';
import pushRoute from '@utils/pushRoute';
import { getLink, getLinkPath } from '@models/Item';
import useClassVideo from './hooks/useClassVideo';
import ClassCard from './ClassesVideo/ClassCard';

const LottieAnimation = Dynamic(() =>
  import('@base/LottieAnimation/LottieAnimation')
);

const SecContainer = styled.div`
  width: 100%;
  padding: ${Spacings.SPACING_12B} 0;
  background-image: url('${getImage('goal/FullGrid_v1.svg')}'),
    url('${getImage('goal/FullGrid_v2.svg')}');
  background-color: var(--color-i-violet-dark-1);
  background-repeat: no-repeat;
  background-position: top left, bottom right;

  ${mediaQuery.uptoTablet} {
    padding: ${Spacings.SPACING_8B} 0 ${Spacings.SPACING_8B};
    background: url('${getImage('goal/FullGrid_mWeb.svg')}')
      var(--color-i-violet-dark-1) no-repeat;
    background-position: top right;
  }

  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_8B} 0 ${Spacings.SPACING_4B};
    background: url('${getImage('goal/FullGrid_mWeb.svg')}')
      var(--color-i-violet-dark-1) no-repeat;
    background-position: top right;
  }
`;

const SecContainerInner = styled.div`
  position: relative;
  width: var(--layout-width);
  margin: auto;

  ${mediaQuery.uptoTablet} {
    width: 100%;
  }
`;

const SocialProofContainer = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  padding: ${Spacings.SPACING_11B} 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${mediaQuery.uptoTablet} {
    right: ${Spacings.SPACING_8B};
  }

  ${mediaQuery.uptoMobile} {
    position: relative;
    padding: 0 0 0 ${Spacings.SPACING_26B};
    align-items: flex-start;
    left: 0;
  }
`;

const HeaderWrapper = styled.div`
  width: 232px;

  ${mediaQuery.uptoTablet} {
    width: 100%;
    padding: 0 ${Spacings.SPACING_8B};
  }

  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: 0 ${Spacings.SPACING_4B};
  }
`;

const Points = styled.div`
  position: relative;
  display: flex;
  gap: ${Spacings.SPACING_3B};
  margin-top: ${Spacings.SPACING_3B};

  ${mediaQuery.uptoTablet} {
    width: 100%;
    padding: 0 ${Spacings.SPACING_8B};
    flex-direction: column;
  }

  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: 0 ${Spacings.SPACING_4B};
    flex-direction: column;
  }
`;

const Point = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const DividerWrapper1 = styled.div`
  padding: ${Spacings.SPACING_6B} 0;

  ${mediaQuery.uptoTablet} {
    width: var(--layout-tab-width);
    margin-inline: auto;
    padding: ${Spacings.SPACING_6B} 0 ${Spacings.SPACING_1B};
  }

  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const DividerWrapper2 = styled.div`
  display: none;

  ${mediaQuery.uptoMobile} {
    display: block;
    width: 90%;
    margin: auto;
    padding: ${Spacings.SPACING_4B} 0 ${Spacings.SPACING_3B};
  }
`;

const CarouselContainer = styled.div`
  width: var(--layout-width);
  position: relative;

  ${mediaQuery.uptoTablet} {
    width: var(--layout-tab-width);
    display: none;
  }
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const ClassVideoContainer = styled.div`
  width: var(--layout-width);

  iframe {
    position: relative;
    height: 523px;
    width: 100%;
    z-index: 1;
  }
`;

const StyledArrowButton = styled(ArrowButton)`
  position: relative;
  border: none;

  :nth-of-type(2) {
    margin-left: 12px;
  }
`;

const ButtonWrapper = styled.div`
  position: absolute;
  left: 577px;
  right: ${({ $isLoggedIn }) => ($isLoggedIn ? '0' : 'unset')};
  bottom: 0;
  top: ${({ $isLoggedIn }) => ($isLoggedIn ? '84px' : 'unset')};
  z-index: 1;
  display: flex;
  justify-content: flex-end;
  height: ${Spacings.SPACING_8B};

  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const ContentWrapper = styled.div`
  height: 100%;
  /* flex-basis: calc(100% - 561px); */
  padding: ${Spacings.SPACING_4B} 0;
`;
const ClassTopicAndLangInfo = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${(props) => props.theme.measurements.spacing(4)};
  align-items: center;
`;
const ClassName = styled.div`
  margin: ${Spacings.SPACING_2B} 0;
`;
const ClassTopicName = styled(Typography)`
  color: ${(props) => props.$topicColor || props.theme.palette.VIOLET};
  font-size: ${(props) => props.theme.measurements.spacing(6)};
  font-weight: 700;
  line-height: ${(props) => props.theme.measurements.spacing(6)};
  text-transform: uppercase;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const EducatorName = styled.div`
  margin-bottom: ${Spacings.SPACING_2B};
`;

const CardsWrapper = styled.div`
  display: none;
  overscroll-behavior: unset;
  ::-webkit-scrollbar {
    display: none;
  }

  ${mediaQuery.uptoTablet} {
    display: flex;
    grid-gap: 0;
    overflow: scroll;
    margin-top: ${Spacings.SPACING_5B};

    > * {
      box-sizing: content-box;
      padding: 0 ${Spacings.SPACING_2B};
      :first-of-type {
        padding-left: ${Spacings.SPACING_8B};
      }
      :last-of-type {
        padding-right: ${Spacings.SPACING_8B};
      }
    }
  }
  ${mediaQuery.uptoMobile} {
    display: flex;
    grid-gap: 0;
    overflow: scroll;
    margin-top: ${Spacings.SPACING_5B};

    > * {
      box-sizing: content-box;
      padding: 0 ${Spacings.SPACING_2B};
      :first-of-type {
        padding-left: ${Spacings.SPACING_4B};
      }
      :last-of-type {
        padding-right: ${Spacings.SPACING_4B};
      }
    }
  }
`;

const ScreenWrapper = styled.div`
  &.desktop {
    display: block;
    position: relative;
    margin: 0 auto;
    ${mediaQuery.uptoTablet} {
      width: var(--layout-tab-width);
    }
    ${mediaQuery.uptoMobile} {
      display: none;
      width: unset;
    }
  }
  &.mobile {
    display: none;
    ${mediaQuery.uptoMobile} {
      display: block;
    }
  }
`;

const AnimationContainer = styled(LottieAnimation)`
  height: ${Spacings.SPACING_8B};
  width: ${Spacings.SPACING_20B};

  ${mediaQuery.uptoMobile} {
    position: absolute;
    left: ${Spacings.SPACING_4B};
    top: 50%;
    transform: translateY(-50%);
  }
`;

const AnimationLabelWrapper = styled.div`
  margin-top: 9px;
`;
const AnimationSubLabelWrapper = styled.div`
  margin-top: 3px;
`;

const VideoPlayerWrapper = styled.div`
  border: ${Spacings.SPACING_1} solid var(--color-i-divider);
  border-radius: ${Spacings.SPACING_4B};
  overflow: hidden;
  display: flex;
`;
const VideoPlayer = ({ classInfo }) => {
  useClassVideo(
    { ...classInfo?.nextSession?.properties, author: classInfo?.author } || {}
  );

  return (
    <VideoPlayerWrapper>
      <ClassVideoPlayer internalFrame />
    </VideoPlayerWrapper>
  );
};

const ClassesVideo = ({ block, socialProof = {} }) => {
  const { data: classes } = block;

  const { specialClassesWatched = 0 } = socialProof;

  const isLoggedIn = useMyInfo(getIsLoggedIn);

  const carouselRef = useRef(null);
  const [currSlide, setSlide] = useState(0);

  const handleNext = () => {
    if (carouselRef.current) {
      carouselRef.current.onNext();
      setSlide(currSlide + 1);
    }
  };

  const handlePrev = () => {
    if (carouselRef.current) {
      carouselRef.current.onPrev();
      setSlide(currSlide - 1);
    }
  };
  const handleClick = (classInfo) => {
    const href = getLinkPath();
    const as = getLink({
      ...classInfo,
      nextTrack: {
        ...classInfo.nextSession,
        ...classInfo.nextSession?.properties
      }
    });
    pushRoute(href, as);
  };

  const isMobile = deviceHelper.isMobileDevice();
  const learners = Math.round((specialClassesWatched / 1000) * 10) / 10;
  return (
    <SecContainer $isLoggedIn={isLoggedIn}>
      <SecContainerInner>
        <HeaderWrapper>
          <Typography variant="h3">Watch free online classes</Typography>
        </HeaderWrapper>
        <Points>
          <Point>
            <CheckContained width={17} height={17} />
            <Typography variant="p2">Chat live with educators </Typography>
          </Point>
          <Point>
            <CheckContained width={17} height={17} />
            <Typography variant="p2">Attempt interactive polls</Typography>
          </Point>
          <Point>
            <CheckContained width={17} height={17} />
            <Typography variant="p2">Get your doubts cleared</Typography>
          </Point>
        </Points>
        {learners && !isMobile ? (
          <ScreenWrapper className="desktop">
            <SocialProofContainer>
              <AnimationContainer
                src={getAnimationPath('goalPage/joiningMembers.json')}
              />
              <AnimationLabelWrapper>
                <Typography variant="h4">{learners}K learners</Typography>
              </AnimationLabelWrapper>
              <AnimationSubLabelWrapper>
                <Typography variant="p2" color="var(--color-text-secondary)">
                  watched a class today
                </Typography>
              </AnimationSubLabelWrapper>
            </SocialProofContainer>
          </ScreenWrapper>
        ) : null}
        <DividerWrapper1>
          <Divider />
        </DividerWrapper1>
        <CarouselContainer>
          <ButtonWrapper $isLoggedIn={isLoggedIn}>
            <StyledArrowButton
              type="prev"
              disabled={currSlide === 0}
              onClick={handlePrev}
            />
            <StyledArrowButton
              type="next"
              disabled={currSlide === classes.length - 1}
              onClick={handleNext}
            />
          </ButtonWrapper>
          <Carousel
            ref={carouselRef}
            itemsPerSlide={1}
            variant="single"
            hideButton
          >
            {classes.map((classInfo, i) => {
              if (!classInfo.nextSession) return null;
              if (isLoggedIn) {
                return (
                  <ClassVideoContainer key={classInfo.uid}>
                    <ContentWrapper>
                      <ClassTopicAndLangInfo>
                        <Tag variant="default" size="small">
                          {classInfo.languageDisplay}
                        </Tag>
                        <ClassTopicName
                          variant="p2"
                          $topicColor={classInfo.topicColor || '#8873FF'}
                        >
                          {classInfo.topicName}
                        </ClassTopicName>
                      </ClassTopicAndLangInfo>
                      <ClassName>
                        <Typography variant="h3">{classInfo.name}</Typography>
                      </ClassName>
                      <EducatorName>
                        <Typography
                          variant="p2"
                          color="var(--color-text-secondary)"
                        >
                          {classInfo.educatorName}
                        </Typography>
                      </EducatorName>
                    </ContentWrapper>
                    {i === currSlide && classInfo.nextSession ? (
                      <VideoPlayer internalFrame classInfo={classInfo} />
                    ) : (
                      <SkeletonCore variant="rect" height={523} width="100%" />
                    )}
                  </ClassVideoContainer>
                );
              }
              return <ClassCard key={classInfo.uid} classInfo={classInfo} />;
            })}
          </Carousel>
        </CarouselContainer>
        <CardsWrapper>
          {classes.map((classInfo) => {
            if (!classInfo.nextSession) return null;
            return (
              <ClassCardAquila
                key={classInfo.uid}
                as="a"
                title={classInfo.title}
                educatorName={classInfo.educatorName}
                educatorLink={classInfo.educatorLink}
                topicName={classInfo.topicName}
                topicColor={classInfo.topicColor || '#8873FF'}
                langLabel={classInfo.langLabel}
                thumbnail={classInfo.thumbnail}
                viewCount={classInfo.viewCount}
                detailsLine={classInfo.detailsLine}
                variant={classInfo.variant}
                onClick={() => handleClick(classInfo)}
              />
            );
          })}
        </CardsWrapper>
        <DividerWrapper2>
          <Divider />
        </DividerWrapper2>
        {learners && isMobile ? (
          <ScreenWrapper className="mobile">
            <SocialProofContainer>
              <AnimationContainer
                src={getAnimationPath('goalPage/joiningMembers.json')}
              />
              <Typography variant="h6">{learners}K learners</Typography>
              <Typography variant="p3" color="var(--color-text-secondary)">
                watched a class today
              </Typography>
            </SocialProofContainer>
          </ScreenWrapper>
        ) : null}
      </SecContainerInner>
    </SecContainer>
  );
};

export default ClassesVideo;
